/**
 * Language Learning Journey Data Structure
 *
 * Defines the curriculum for English language progression
 * 60 days per CEFR level transition (e.g., B1→C1 = 120 days)
 * Default target: C1 (Graduate-ready level)
 *
 * Multi-Level Support:
 * - Journey spans multiple levels if needed (e.g., B1→B2→C1)
 * - Courses are cycled through; when exhausted, marked as "revision"
 * - Users are informed when doing revision courses
 *
 * Daily Activities:
 * - Speaking practice with Liwaza AI (admin session length, default 9 minutes)
 * - Grammar and vocabulary lessons based on level
 * - Weekly checkpoints (tests) at end of each week
 */

// Import speaking topics from the JSON files
import speakingTopicsData from '../assets/mock-data/speaking.json';
import structuredTopicsData from '../assets/mock-data/structured-speaking-topics.json';
import {
  getAccountScopedLocalStorageKey,
  setLocalStorageWithSync,
  syncLocalStorageToBackend,
  fetchBackendConfiguration,
} from './utils';
import { invalidateHomeMandatorySnapshot } from '../core/home/homeSnapshot';
import {
  getJourneyDayOverride,
  getSpeakingPracticeMinutes,
  getVoiceLimitsStorageKey,
} from './voiceMinutesManager';
import {
  countHoldDaysOverlapping,
  getStoredHoldIntervals,
  loadJourneyHoldIntervals,
  toDateOnly,
} from './progressHold';
import {
  CEFR_TO_DIFFICULTY,
  CEFR_TO_DIFFICULTY_RANGE,
  CEFR_TO_IELTS,
  CEFR_ORDER,
  canAccessAcademicTests,
  canAccessFullMocks,
  isPreIeltsLevel,
} from './cefrUtils';
import {
  GRAMMAR_LESSONS,
  VOCABULARY_LESSONS,
  IELTS_SKILLS_COURSES,
  READING_TESTS,
  WRITING_TESTS,
  LISTENING_TESTS,
  MOCK_EXAMS,
  getLessonsForCefr,
  getMocksForCefr,
} from './ieltsCatalog';
import { fetchIeltsProfile, updateIeltsProfile } from './ieltsProfile';

export {
  GRAMMAR_LESSONS,
  VOCABULARY_LESSONS,
  IELTS_SKILLS_COURSES,
  READING_TESTS,
  WRITING_TESTS,
  LISTENING_TESTS,
  MOCK_EXAMS,
  CEFR_TO_DIFFICULTY,
  CEFR_TO_DIFFICULTY_RANGE,
  CEFR_TO_IELTS,
};

// Journey version - increment when structure changes to force regeneration
export const JOURNEY_VERSION = 7;

/**
 * Generate a hash code from a string (for seeding random)
 * @param {string} str - String to hash
 * @returns {number} Hash code
 */
const hashCode = (str) => {
  let hash = 0;
  if (!str || str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Seeded random number generator (Linear Congruential Generator)
 * Returns a function that generates deterministic random numbers 0-1
 * @param {number} seed - Seed value
 * @returns {function} Random number generator function
 */
const createSeededRandom = (seed) => {
  let currentSeed = seed;
  return () => {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    return currentSeed / 233280;
  };
};

/**
 * Shuffle array with seeded random (deterministic - same seed = same result)
 * @param {Array} array - Array to shuffle
 * @param {function} randomFn - Seeded random function
 * @returns {Array} Shuffled array
 */
const shuffleArraySeeded = (array, randomFn) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(randomFn() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Legacy shuffle function (non-deterministic) - kept for backward compatibility
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Get conversational speaking topics for the journey, ordered by difficulty.
 * Topics are grouped by difficulty (1-5), shuffled within each group, then
 * concatenated so easier topics appear earlier in the journey.
 * Cycles through if more are needed than available.
 * @param {number} count - Number of topics to get
 * @param {function} randomFn - Seeded random function (optional, falls back to Math.random)
 * @returns {Array} Array of speaking topics
 */
const getUniqueSpeakingTopics = (count = 60, randomFn = null) => {
  // Group topics by difficulty
  const groups = {};
  speakingTopicsData.forEach((t) => {
    const d = t.difficulty || 2;
    if (!groups[d]) groups[d] = [];
    groups[d].push(t);
  });
  // Shuffle within each group for variety
  const shuffleFn = randomFn
    ? (arr) => shuffleArraySeeded(arr, randomFn)
    : shuffleArray;
  Object.keys(groups).forEach((d) => {
    groups[d] = shuffleFn(groups[d]);
  });
  // Concatenate in ascending difficulty order
  const ordered = [];
  [1, 2, 3, 4, 5].forEach((d) => {
    if (groups[d]) ordered.push(...groups[d]);
  });
  // Cycle through if needed
  const topics = [];
  for (let i = 0; i < count; i++) {
    const source = ordered[i % ordered.length];
    topics.push({
      id: source.id,
      category: source.category,
      topic: source.topic,
      vocabulary: source.vocabulary || [],
      difficulty: source.difficulty || 2,
      speakingType: 'conversational',
    });
  }
  return topics;
};

/**
 * Speaking types for structured practice:
 * - describe: IELTS Part 2 style - describe something with cue card prompts
 * - opinion: IELTS Part 3 style - express and defend an opinion
 * - info-based: Answer questions based on given data/passage
 * - conversational: General conversation practice (default)
 */
export const SPEAKING_TYPES = {
  describe: { label: 'Describe', icon: 'image', color: '#3b82f6' },
  opinion: { label: 'Opinion', icon: 'message', color: '#8b5cf6' },
  'info-based': { label: 'Info-Based', icon: 'bar-chart', color: '#f59e0b' },
  conversational: { label: 'Conversation', icon: 'mic', color: '#22c55e' },
};

/**
 * Get structured speaking topics (describe, opinion, info-based)
 * Ordered by difficulty so earlier weeks get easier topics.
 * Uses seeded random for deterministic results per user.
 * @param {number} weeksNeeded - Number of weeks to generate topics for
 * @param {function} randomFn - Seeded random function
 * @returns {object} { describe: [...], opinion: [...], infoBased: [...] }
 */
const getStructuredSpeakingTopics = (weeksNeeded, randomFn = null) => {
  const orderByDiff = (items) => {
    const groups = {};
    items.forEach((t) => {
      const d = t.difficulty || 3;
      if (!groups[d]) groups[d] = [];
      groups[d].push(t);
    });
    const shuffleFn = randomFn
      ? (arr) => shuffleArraySeeded(arr, randomFn)
      : shuffleArray;
    Object.keys(groups).forEach((d) => {
      groups[d] = shuffleFn(groups[d]);
    });
    const ordered = [];
    [1, 2, 3, 4, 5].forEach((d) => {
      if (groups[d]) ordered.push(...groups[d]);
    });
    return ordered;
  };

  const describeTopics = orderByDiff([...structuredTopicsData.describe]);
  const opinionTopics = orderByDiff([...structuredTopicsData.opinion]);
  const infoBasedTopics = orderByDiff([...structuredTopicsData.infoBased]);

  const getTopicCycled = (pool, index) => pool[index % pool.length];

  return {
    describe: Array.from({ length: weeksNeeded }, (_, i) =>
      getTopicCycled(describeTopics, i)
    ),
    opinion: Array.from({ length: weeksNeeded }, (_, i) =>
      getTopicCycled(opinionTopics, i)
    ),
    infoBased: Array.from({ length: weeksNeeded }, (_, i) =>
      getTopicCycled(infoBasedTopics, i)
    ),
  };
};

/**
 * For a given week, pick 3 random weekday positions (1-6 = Mon-Sat) for structured speaking types.
 * Returns an object mapping dayOfWeek -> speakingType
 * @param {function} randomFn - Seeded random function
 * @returns {object} e.g. { 2: 'describe', 4: 'opinion', 5: 'info-based' }
 */
const pickStructuredDays = (randomFn) => {
  const weekdays = [1, 2, 3, 4, 5, 6]; // Mon-Sat
  const shuffled = randomFn
    ? shuffleArraySeeded([...weekdays], randomFn)
    : shuffleArray([...weekdays]);

  return {
    [shuffled[0]]: 'describe',
    [shuffled[1]]: 'opinion',
    [shuffled[2]]: 'info-based',
  };
};

const selectTestByDifficulty = (tests, testIndex, fromLevel) => {
  if (isPreIeltsLevel(fromLevel)) {
    return null;
  }
  const range = CEFR_TO_DIFFICULTY_RANGE[fromLevel] || { min: 2, max: 4 };
  const allowAcademic = canAccessAcademicTests(fromLevel);
  const suitable = tests.filter((t) => {
    if (t.difficulty < range.min || t.difficulty > range.max) return false;
    if (!allowAcademic && t.format === 'academic') return false;
    return true;
  });
  if (suitable.length > 0) {
    return suitable[testIndex % suitable.length];
  }
  const fallback = tests.filter((t) => allowAcademic || t.format !== 'academic');
  return fallback[testIndex % fallback.length] || tests[testIndex % tests.length];
};

// Return all grammar lessons (already a flat array with difficulty scores)
const getAllGrammarLessons = () => GRAMMAR_LESSONS;

// Return all vocabulary lessons (already a flat array with difficulty scores)
const getAllVocabularyLessons = () => VOCABULARY_LESSONS;

// Combine all IELTS skills courses into arrays
const getAllListeningCourses = () => IELTS_SKILLS_COURSES.listening;
const getAllReadingCourses = () => IELTS_SKILLS_COURSES.reading;
const getAllWritingCourses = () => IELTS_SKILLS_COURSES.writing;
const getAllSpeakingCourses = () => IELTS_SKILLS_COURSES.speaking;

// Get the next CEFR level
// eslint-disable-next-line no-unused-vars
const getNextLevel = (level) => {
  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const currentIndex = levels.indexOf(level);
  if (currentIndex === -1 || currentIndex >= levels.length - 1) return null;
  return levels[currentIndex + 1];
};

/**
 * Default IELTS target level - most graduate programs require C1
 */
export const DEFAULT_IELTS_TARGET = 'C1';

/**
 * Days per CEFR level transition
 */
export const DAYS_PER_LEVEL = 60;

/**
 * CEFR level order for progression
 */
const CEFR_ORDER_LOCAL = CEFR_ORDER;

/**
 * Calculate the appropriate journey target level
 * Default target is C1 unless already at C1 or above
 * @param {string} currentLevelCode - Current CEFR level code
 * @returns {string} Target CEFR level code
 */
export const getJourneyTarget = (currentLevelCode) => {
  const currentIndex = CEFR_ORDER_LOCAL.indexOf(currentLevelCode);
  const targetIndex = CEFR_ORDER_LOCAL.indexOf(DEFAULT_IELTS_TARGET);

  // If already at or above default target, aim for next level
  if (currentIndex >= targetIndex) {
    return currentIndex < CEFR_ORDER_LOCAL.length - 1
      ? CEFR_ORDER_LOCAL[currentIndex + 1]
      : 'C2';
  }

  return DEFAULT_IELTS_TARGET;
};

/**
 * Get the level path between current and target (e.g., ['B1', 'B2', 'C1'])
 * @param {string} currentLevel - Current CEFR level code
 * @param {string} targetLevel - Target CEFR level code
 * @returns {Array<string>} Array of level codes in the path
 */
export const getLevelPath = (currentLevel, targetLevel) => {
  const currentIndex = CEFR_ORDER_LOCAL.indexOf(currentLevel);
  const targetIndex = CEFR_ORDER_LOCAL.indexOf(targetLevel);

  if (
    currentIndex === -1 ||
    targetIndex === -1 ||
    currentIndex >= targetIndex
  ) {
    return [currentLevel];
  }

  return CEFR_ORDER_LOCAL.slice(currentIndex, targetIndex + 1);
};

/**
 * Calculate total journey days for a level transition
 * @param {string} currentLevel - Current CEFR level code
 * @param {string} targetLevel - Target CEFR level code
 * @returns {number} Total days needed
 */
export const calculateTotalJourneyDays = (currentLevel, targetLevel) => {
  const currentIndex = CEFR_ORDER_LOCAL.indexOf(currentLevel);
  const targetIndex = CEFR_ORDER_LOCAL.indexOf(targetLevel);
  const levelsToAdvance = Math.max(1, targetIndex - currentIndex);
  return levelsToAdvance * DAYS_PER_LEVEL;
};

/**
 * Per-step durations for the enrollment path, e.g. B1→B2 and B2→C1.
 */
export const getLevelTransitionDurations = (currentLevel, targetLevel) => {
  const path = getLevelPath(currentLevel, targetLevel);
  if (path.length < 2) {
    return [
      {
        from: currentLevel,
        to: targetLevel || currentLevel,
        days: DAYS_PER_LEVEL,
      },
    ];
  }
  const transitions = [];
  for (let i = 0; i < path.length - 1; i += 1) {
    transitions.push({
      from: path[i],
      to: path[i + 1],
      days: DAYS_PER_LEVEL,
    });
  }
  return transitions;
};

/**
 * Get the level segment info for a specific day in the journey
 * @param {number} dayNumber - Day number (1-based)
 * @param {string} currentLevel - Starting CEFR level code
 * @param {string} targetLevel - Target CEFR level code
 * @returns {object} { from, to, segmentNumber, totalSegments, isFirstDayOfSegment, isLastDayOfSegment }
 */
export const getLevelSegmentForDay = (dayNumber, currentLevel, targetLevel) => {
  const currentIndex = CEFR_ORDER_LOCAL.indexOf(currentLevel);
  const targetIndex = CEFR_ORDER_LOCAL.indexOf(targetLevel);
  const totalSegments = Math.max(1, targetIndex - currentIndex);
  const segmentIndex = Math.min(
    Math.floor((dayNumber - 1) / DAYS_PER_LEVEL),
    totalSegments - 1
  );

  return {
    from: CEFR_ORDER_LOCAL[currentIndex + segmentIndex],
    to: CEFR_ORDER_LOCAL[
      Math.min(currentIndex + segmentIndex + 1, CEFR_ORDER_LOCAL.length - 1)
    ],
    segmentNumber: segmentIndex + 1,
    totalSegments,
    isFirstDayOfSegment: (dayNumber - 1) % DAYS_PER_LEVEL === 0,
    isLastDayOfSegment: dayNumber % DAYS_PER_LEVEL === 0,
  };
};

export const generateJourneyPlan = (
  currentLevel,
  targetLevel,
  startDate = new Date(),
  userEmail = null
) => {
  // Calculate levels to advance and total days
  const currentIndex = CEFR_ORDER_LOCAL.indexOf(currentLevel);
  const targetIndex = CEFR_ORDER_LOCAL.indexOf(targetLevel);
  const levelsToAdvance = Math.max(1, targetIndex - currentIndex);
  const totalDays = levelsToAdvance * DAYS_PER_LEVEL;

  // Create seeded random generator for deterministic journey generation
  // This ensures the same user always gets the same journey order
  const startDateStr =
    startDate instanceof Date
      ? startDate.toISOString().split('T')[0]
      : new Date(startDate).toISOString().split('T')[0];
  const seedString = `${userEmail || 'default'}_${currentLevel}_${targetLevel}_${startDateStr}`;
  const seed = hashCode(seedString);
  const randomFn = createSeededRandom(seed);

  console.log(
    `Generating journey with seed: "${seedString}" (hash: ${seed}), totalDays: ${totalDays}, levels: ${levelsToAdvance}`
  );

  // --- Difficulty-based content ordering per segment ---
  // Content is ordered so that each CEFR segment targets the right difficulty:
  //   A1→A2 segment → diff 1-2,  A2→B1 → diff 2-3,  B1→B2 → diff 3-4,  B2→C1 → diff 4-5
  // For each segment we put matching-difficulty items first (shuffled),
  // then adjacent difficulties, then remaining — so the index naturally
  // walks through appropriate content as the journey progresses.

  /**
   * Get content ordered for a specific CEFR segment.
   * @param {Array} allContent - All content items (each must have a `difficulty` field)
   * @param {number} segmentIndex - 0-based segment within the journey
   * @param {number} currentCefrIdx - 0-based index of the starting CEFR level in CEFR_ORDER_LOCAL
   * @param {function} rng - Seeded random function
   * @returns {Array} Content sorted: target difficulty first, then adjacent, then rest
   */
  /**
   * Get the target difficulty range for a given segment.
   * Each segment maps to a CEFR transition (e.g., A2→B1 = diff 2-3).
   */
  const getSegmentDifficultyRange = (segmentIndex, currentCefrIdx) => {
    const fromLevel =
      CEFR_ORDER_LOCAL[currentCefrIdx + segmentIndex] || currentLevel;
    const range = CEFR_TO_DIFFICULTY_RANGE[fromLevel] || { min: 2, max: 4 };
    return { minDiff: range.min, maxDiff: range.max, fromLevel };
  };

  /**
   * Build a content array ordered across all segments for the full journey.
   *
   * Strategy: Each segment claims ONLY its primary-difficulty items (items
   * whose difficulty falls within the segment's target range). This ensures
   * that harder content is reserved for later segments instead of being
   * consumed early. Items not claimed by any segment as primary are appended
   * at the end (sorted by difficulty ascending) as spillover.
   */
  const buildSegmentContent = (allContent, seedOffset) => {
    const result = [];
    const usedIds = new Set();
    const allowedIds = new Set();

    for (let seg = 0; seg < levelsToAdvance; seg++) {
      const fromLevel = CEFR_ORDER_LOCAL[currentIndex + seg] || currentLevel;
      getLessonsForCefr(fromLevel, allContent).forEach((item) =>
        allowedIds.add(item.id)
      );
    }

    for (let seg = 0; seg < levelsToAdvance; seg++) {
      const { minDiff, maxDiff, fromLevel } = getSegmentDifficultyRange(
        seg,
        currentIndex
      );
      const rng = createSeededRandom(seed + seedOffset + seg * 100);
      const allowed = getLessonsForCefr(fromLevel, allContent);

      const primary = allowed.filter(
        (item) =>
          !usedIds.has(item.id) &&
          (item.difficulty || 3) >= minDiff &&
          (item.difficulty || 3) <= maxDiff
      );
      const shuffled = shuffleArraySeeded([...primary], rng);

      for (const item of shuffled) {
        result.push(item);
        usedIds.add(item.id);
      }
    }

    const unclaimed = allContent
      .filter((item) => !usedIds.has(item.id) && allowedIds.has(item.id))
      .sort((a, b) => (a.difficulty || 3) - (b.difficulty || 3));

    const spilloverRng = createSeededRandom(seed + seedOffset + 999);
    const shuffledUnclaimed = shuffleArraySeeded([...unclaimed], spilloverRng);

    for (const item of shuffledUnclaimed) {
      result.push(item);
      usedIds.add(item.id);
    }

    return result.length > 0 ? result : allContent;
  };

  const allGrammarLessons = buildSegmentContent(getAllGrammarLessons(), 1);
  const allVocabLessons = buildSegmentContent(getAllVocabularyLessons(), 2);
  const allListeningCourses = buildSegmentContent(getAllListeningCourses(), 3);
  const allReadingCourses = buildSegmentContent(getAllReadingCourses(), 4);
  const allWritingCourses = buildSegmentContent(getAllWritingCourses(), 5);
  const allSpeakingCourses = buildSegmentContent(getAllSpeakingCourses(), 6);

  // Set-based revision tracking: a course is only "revision" if it was already
  // assigned to a previous day in THIS journey. Users who start at B1+ won't
  // see A1-A2 content labelled as revision since they never encountered it.
  const assignedGrammarIds = new Set();
  const assignedVocabIds = new Set();
  const assignedListeningIds = new Set();
  const assignedReadingIds = new Set();
  const assignedWritingIds = new Set();
  const assignedSpeakingIds = new Set();

  // Get conversational speaking topics for the full journey duration
  const uniqueSpeakingTopics = getUniqueSpeakingTopics(
    totalDays,
    createSeededRandom(seed + 7)
  );

  // Get structured speaking topics (describe, opinion, info-based) for each week
  const totalWeeks = Math.ceil(totalDays / 7);
  const structuredTopics = getStructuredSpeakingTopics(
    totalWeeks,
    createSeededRandom(seed + 8)
  );

  // Pre-compute which days of each week get structured speaking types
  // Each week, 3 random weekdays (Mon-Sat) get structured types, 3 stay conversational
  const weekStructuredDays = {};
  for (let w = 1; w <= totalWeeks; w++) {
    weekStructuredDays[w] = pickStructuredDays(
      createSeededRandom(seed + 100 + w)
    );
  }

  const journey = [];

  let grammarIndex = 0;
  let vocabIndex = 0;
  let listeningCourseIndex = 0;
  let readingCourseIndex = 0;
  let writingCourseIndex = 0;
  let speakingCourseIndex = 0;
  let readingTestIndex = 0;
  let writingTestIndex = 0;
  let listeningTestIndex = 0;
  let describeIndex = 0;
  let opinionIndex = 0;
  let infoBasedIndex = 0;
  let mockExamIndex = 0;
  let fullTestWeekCount = 0;

  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + day - 1);

    const dayOfWeek = ((day - 1) % 7) + 1; // 1-7 (Mon-Sun)
    const weekNumber = Math.ceil(day / 7);
    const isCheckpoint = dayOfWeek === 7; // Sunday is weekly test day

    // Determine if this is a revision week or a full test week
    // Long journeys (>120 days): 3 revision weeks + 1 test week per 4-week block
    // Short journeys (<=120 days): alternating revision/test weeks
    const isLongJourney = totalDays > 120;
    const weekInBlock = ((weekNumber - 1) % 4) + 1; // 1-4
    const isRevisionWeek = isLongJourney
      ? weekInBlock !== 4 // weeks 1-3 are revision, week 4 is full test
      : weekNumber % 2 === 1; // odd weeks are revision, even are full test

    // Determine if this day has a structured speaking type
    const structuredType = weekStructuredDays[weekNumber]?.[dayOfWeek] || null;

    // Calculate which level segment this day belongs to
    const segmentIndex = Math.min(
      Math.floor((day - 1) / DAYS_PER_LEVEL),
      levelsToAdvance - 1
    );
    const segmentFrom = CEFR_ORDER_LOCAL[currentIndex + segmentIndex];
    const segmentTo =
      CEFR_ORDER_LOCAL[
        Math.min(currentIndex + segmentIndex + 1, CEFR_ORDER_LOCAL.length - 1)
      ];
    const isFirstDayOfSegment = day > 1 && (day - 1) % DAYS_PER_LEVEL === 0;

    // Get current lessons/courses (cycle through with modulo)
    // Note: revision set tracking is deferred to inside each day-of-week block
    // so that a content item is only registered when it's actually assigned.
    const grammarLesson =
      allGrammarLessons[grammarIndex % allGrammarLessons.length];
    const vocabLesson = allVocabLessons[vocabIndex % allVocabLessons.length];
    const writingCourse =
      allWritingCourses[writingCourseIndex % allWritingCourses.length];
    const readingCourse =
      allReadingCourses[readingCourseIndex % allReadingCourses.length];
    const listeningCourse =
      allListeningCourses[listeningCourseIndex % allListeningCourses.length];
    const speakingCourse =
      allSpeakingCourses[speakingCourseIndex % allSpeakingCourses.length];

    const readingTest = selectTestByDifficulty(
      READING_TESTS,
      readingTestIndex,
      segmentFrom
    );
    const writingTest = selectTestByDifficulty(
      WRITING_TESTS,
      writingTestIndex,
      segmentFrom
    );
    const listeningTest = selectTestByDifficulty(
      LISTENING_TESTS,
      listeningTestIndex,
      segmentFrom
    );

    // Use topic for each day
    const speakingTopic = uniqueSpeakingTopics[day - 1];

    const dayPlan = {
      day,
      date: date.toISOString(),
      weekNumber,
      dayOfWeek,
      isCheckpoint,
      status: 'locked', // 'locked', 'available', 'completed', 'current', 'partial'
      activities: [],
      speakingCategory: speakingTopic.category,
      speakingTopic: speakingTopic.topic,
      // Level segment info
      levelSegment: { from: segmentFrom, to: segmentTo },
      segmentNumber: segmentIndex + 1,
      totalSegments: levelsToAdvance,
      isFirstDayOfSegment,
      // Validation requirements
      validationCriteria: {
        requireSpeaking: true,
        requireMinScore: 80, // 80% minimum on exercises/tests
      },
    };

    // Daily speaking practice uses the learner's session length (default 9 minutes).
    // Checkpoint days use the speaking test instead.
    // 3 random days per week have structured speaking types, 3 are conversational
    if (!isCheckpoint) {
      const speakingActivity = {
        id: `speaking-day-${day}`,
        type: 'speaking',
        name: 'Daily Speaking Practice',
        duration: getSpeakingPracticeMinutes(),
        topicId: speakingTopic.id,
        topic: speakingTopic.topic,
        category: speakingTopic.category,
        vocabulary: speakingTopic.vocabulary,
        path: '/exams/english/speaking',
        completed: false,
        score: null,
        required: true,
        speakingType: 'conversational',
        description: `Practice: ${speakingTopic.category}`,
      };

      // Override with structured topic if this day is assigned one
      if (structuredType === 'describe') {
        const describeTopic =
          structuredTopics.describe[
            describeIndex % structuredTopics.describe.length
          ];
        speakingActivity.speakingType = 'describe';
        speakingActivity.topic = describeTopic.topic;
        speakingActivity.topicId = describeTopic.id;
        speakingActivity.category = describeTopic.category;
        speakingActivity.vocabulary = describeTopic.vocabulary;
        speakingActivity.cueCard = describeTopic.cueCard;
        speakingActivity.name = 'Describe & Explain';
        speakingActivity.description = `Describe: ${describeTopic.category}`;
        describeIndex++;
      } else if (structuredType === 'opinion') {
        const opinionTopic =
          structuredTopics.opinion[
            opinionIndex % structuredTopics.opinion.length
          ];
        speakingActivity.speakingType = 'opinion';
        speakingActivity.topic = opinionTopic.topic;
        speakingActivity.topicId = opinionTopic.id;
        speakingActivity.category = opinionTopic.category;
        speakingActivity.vocabulary = opinionTopic.vocabulary;
        speakingActivity.opinionPrompt = opinionTopic.opinionPrompt;
        speakingActivity.name = 'Express Your Opinion';
        speakingActivity.description = `Opinion: ${opinionTopic.category}`;
        opinionIndex++;
      } else if (structuredType === 'info-based') {
        const infoTopic =
          structuredTopics.infoBased[
            infoBasedIndex % structuredTopics.infoBased.length
          ];
        speakingActivity.speakingType = 'info-based';
        speakingActivity.topic = infoTopic.topic;
        speakingActivity.topicId = infoTopic.id;
        speakingActivity.category = infoTopic.category;
        speakingActivity.vocabulary = infoTopic.vocabulary;
        speakingActivity.infoContent = infoTopic.infoContent;
        speakingActivity.name = 'Info-Based Discussion';
        speakingActivity.description = `Analyse: ${infoTopic.infoContent?.title || infoTopic.category}`;
        infoBasedIndex++;
      }

      dayPlan.activities.push(speakingActivity);
    }

    // Non-checkpoint days have courses (NO tests)
    if (!isCheckpoint) {
      // Monday: Grammar lesson
      if (dayOfWeek === 1) {
        const isGrammarRevision = assignedGrammarIds.has(grammarLesson.id);
        assignedGrammarIds.add(grammarLesson.id);
        dayPlan.activities.push({
          id: `grammar-day-${day}`,
          type: 'grammar',
          name: grammarLesson.name,
          lessonId: grammarLesson.id, // Store original lesson ID for matching
          difficulty: grammarLesson.difficulty || 3,
          duration: 20,
          path: grammarLesson.path,
          component: grammarLesson.component,
          completed: false,
          score: null,
          required: true,
          isRevision: isGrammarRevision,
          description: isGrammarRevision
            ? `Revision: ${grammarLesson.name}`
            : `Learn: ${grammarLesson.name}`,
        });
        dayPlan.lessonName = grammarLesson.name;
        dayPlan.lessonType = 'grammar';
        dayPlan.isRevisionDay = isGrammarRevision;
        grammarIndex++;
      }
      // Tuesday: Vocabulary lesson
      else if (dayOfWeek === 2) {
        const isVocabRevision = assignedVocabIds.has(vocabLesson.id);
        assignedVocabIds.add(vocabLesson.id);
        dayPlan.activities.push({
          id: `vocab-day-${day}`,
          type: 'vocabulary',
          name: vocabLesson.name,
          lessonId: vocabLesson.id, // Store original lesson ID for matching
          difficulty: vocabLesson.difficulty || 3,
          duration: 20,
          path: vocabLesson.path,
          component: vocabLesson.component,
          completed: false,
          score: null,
          required: true,
          isRevision: isVocabRevision,
          description: isVocabRevision
            ? `Revision: ${vocabLesson.name}`
            : `Master: ${vocabLesson.name}`,
        });
        dayPlan.lessonName = vocabLesson.name;
        dayPlan.lessonType = 'vocabulary';
        dayPlan.isRevisionDay = isVocabRevision;
        vocabIndex++;
      }
      // Wednesday: Writing Course (skills training, not test)
      else if (dayOfWeek === 3) {
        const isWritingCourseRevision = assignedWritingIds.has(
          writingCourse.id
        );
        assignedWritingIds.add(writingCourse.id);
        dayPlan.activities.push({
          id: `writing-course-day-${day}`,
          type: 'writing-course',
          name: writingCourse.name,
          lessonId: writingCourse.id, // Store original lesson ID for matching
          difficulty: writingCourse.difficulty || 3,
          duration: 25,
          path: writingCourse.path,
          component: writingCourse.component,
          completed: false,
          score: null,
          required: true,
          isCourse: true,
          isRevision: isWritingCourseRevision,
          description: isWritingCourseRevision
            ? `Revision: ${writingCourse.description}`
            : writingCourse.description,
        });
        dayPlan.lessonName = writingCourse.name;
        dayPlan.lessonType = 'writing-course';
        dayPlan.isRevisionDay = isWritingCourseRevision;
        writingCourseIndex++;
      }
      // Thursday: Reading Course (skills training, not test)
      else if (dayOfWeek === 4) {
        const isReadingCourseRevision = assignedReadingIds.has(
          readingCourse.id
        );
        assignedReadingIds.add(readingCourse.id);
        dayPlan.activities.push({
          id: `reading-course-day-${day}`,
          type: 'reading-course',
          name: readingCourse.name,
          lessonId: readingCourse.id, // Store original lesson ID for matching
          difficulty: readingCourse.difficulty || 3,
          duration: 25,
          path: readingCourse.path,
          component: readingCourse.component,
          completed: false,
          score: null,
          required: true,
          isCourse: true,
          isRevision: isReadingCourseRevision,
          description: isReadingCourseRevision
            ? `Revision: ${readingCourse.description}`
            : readingCourse.description,
        });
        dayPlan.lessonName = readingCourse.name;
        dayPlan.lessonType = 'reading-course';
        dayPlan.isRevisionDay = isReadingCourseRevision;
        readingCourseIndex++;
      }
      // Friday: Listening Course (skills training, not test)
      else if (dayOfWeek === 5) {
        const isListeningCourseRevision = assignedListeningIds.has(
          listeningCourse.id
        );
        assignedListeningIds.add(listeningCourse.id);
        dayPlan.activities.push({
          id: `listening-course-day-${day}`,
          type: 'listening-course',
          name: listeningCourse.name,
          lessonId: listeningCourse.id, // Store original lesson ID for matching
          difficulty: listeningCourse.difficulty || 3,
          duration: 25,
          path: listeningCourse.path,
          component: listeningCourse.component,
          completed: false,
          score: null,
          required: true,
          isCourse: true,
          isRevision: isListeningCourseRevision,
          description: isListeningCourseRevision
            ? `Revision: ${listeningCourse.description}`
            : listeningCourse.description,
        });
        dayPlan.lessonName = listeningCourse.name;
        dayPlan.lessonType = 'listening-course';
        dayPlan.isRevisionDay = isListeningCourseRevision;
        listeningCourseIndex++;
      }
      // Saturday: Speaking Course OR Revision Session (every 2 weeks)
      else if (dayOfWeek === 6) {
        const isRevisionSessionWeek = weekNumber % 2 === 0 && weekNumber >= 2;

        if (isRevisionSessionWeek) {
          const weakLessonIds = userEmail
            ? getWeakTopics(userEmail)
                .map((topic) => topic.lessonId)
                .filter(Boolean)
            : [];
          dayPlan.activities.push({
            id: `revision-session-week-${weekNumber}`,
            type: 'revision-session',
            name: 'Revision Session',
            duration: 30,
            path: '/exams/english/revision-session',
            completed: false,
            score: null,
            required: true,
            isRevisionSession: true,
            weakLessonIds,
            description: weakLessonIds.length
              ? 'Review the topics you found challenging'
              : 'Review topics you found challenging',
          });
          dayPlan.lessonName = 'Revision Session';
          dayPlan.lessonType = 'revision-session';
          dayPlan.isRevisionSession = true;
        } else {
          const isSpeakingCourseRevision = assignedSpeakingIds.has(
            speakingCourse.id
          );
          assignedSpeakingIds.add(speakingCourse.id);
          dayPlan.activities.push({
            id: `speaking-course-day-${day}`,
            type: 'speaking-course',
            name: speakingCourse.name,
            lessonId: speakingCourse.id, // Store original lesson ID for matching
            difficulty: speakingCourse.difficulty || 3,
            duration: 20,
            path: speakingCourse.path,
            component: speakingCourse.component,
            completed: false,
            score: null,
            required: true,
            isCourse: true,
            isRevision: isSpeakingCourseRevision,
            description: isSpeakingCourseRevision
              ? `Revision: ${speakingCourse.description}`
              : speakingCourse.description,
          });
          dayPlan.lessonName = speakingCourse.name;
          dayPlan.lessonType = 'speaking-course';
          dayPlan.isRevisionDay = isSpeakingCourseRevision;
          speakingCourseIndex++;
        }
      }
    }

    // Sunday: Weekly checkpoint
    if (isCheckpoint) {
      const forceRevisionCheckpoint = isPreIeltsLevel(segmentFrom);
      if (isRevisionWeek || forceRevisionCheckpoint) {
        // --- REVISION CHECKPOINT: Mini-quiz covering the week's topics ---
        // Gather topics covered in the preceding week(s) for this revision block
        const weeksCovered = isLongJourney
          ? [weekNumber - 2, weekNumber - 1, weekNumber].filter((w) => w >= 1)
          : [weekNumber - 1, weekNumber].filter((w) => w >= 1);

        // Collect topic summaries from this week's journey days
        const weekTopics = journey
          .filter(
            (d) =>
              weeksCovered.includes(d.weekNumber) &&
              !d.isCheckpoint &&
              d.activities
          )
          .map((d) => ({
            lessonName: d.lessonName,
            lessonType: d.lessonType,
            lessonId: d.activities?.find((a) => a.lessonId)?.lessonId || null,
            component:
              d.activities?.find((a) => a.component)?.component || null,
          }))
          .filter((t) => t.lessonName);

        dayPlan.activities.push({
          id: `revision-quiz-week-${weekNumber}`,
          type: 'revision-quiz',
          name: 'Weekly Review Quiz',
          duration: 30,
          path: '/exams/english/revision-session',
          completed: false,
          score: null,
          required: true,
          isTest: true,
          isRevisionCheckpoint: true,
          order: 1,
          weeksCovered,
          topicsToReview: weekTopics,
          description: `Review: Topics from week${weeksCovered.length > 1 ? 's' : ''} ${weeksCovered.join(' & ')}`,
        });

        dayPlan.activities.push({
          id: `listening-review-week-${weekNumber}`,
          type: 'listening-review',
          name: 'Listening review',
          duration: 15,
          path: '/exams/english/listening-review',
          completed: false,
          score: null,
          required: true,
          isTest: true,
          order: 2,
          description: 'Hear a passage and answer questions',
        });

        // Speaking still included on revision weeks
        dayPlan.activities.push({
          id: `speaking-test-week-${weekNumber}`,
          type: 'speaking',
          name: 'Speaking Assessment',
          duration: 15,
          path: '/exams/english/speaking',
          topicId: speakingTopic.id,
          topic: speakingTopic.topic,
          category: speakingTopic.category,
          completed: false,
          score: null,
          required: true,
          isTest: true,
          order: 3,
          description: 'Speaking assessment',
        });

        dayPlan.lessonName = `Week ${weekNumber} Review`;
        dayPlan.lessonType = 'revision-checkpoint';
        dayPlan.isRevisionCheckpoint = true;
      } else {
        fullTestWeekCount += 1;
        const availableMocks = getMocksForCefr(segmentFrom);
        const useFullMock =
          canAccessFullMocks(segmentFrom) &&
          availableMocks.length > 0 &&
          fullTestWeekCount % 2 === 0;

        if (useFullMock) {
          const mockExam = availableMocks[mockExamIndex % availableMocks.length];
          mockExamIndex += 1;
          dayPlan.activities.push({
            id: `mock-exam-week-${weekNumber}`,
            type: 'mock',
            name: `${mockExam.title} — includes Listening`,
            duration: 165,
            path: '/exams/english',
            mockExamId: mockExam.id,
            completed: false,
            score: null,
            required: true,
            isTest: true,
            isMockExam: true,
            order: 1,
            description:
              'Full IELTS mock exam: Listening, Reading, Writing, Speaking',
          });
          dayPlan.lessonName = `Week ${weekNumber} Mock`;
          dayPlan.lessonType = 'mock-checkpoint';
        } else if (listeningTest && readingTest && writingTest) {
          dayPlan.activities.push({
            id: `listening-test-week-${weekNumber}`,
            type: 'listening',
            name: listeningTest.name,
            duration: 40,
            path: listeningTest.path,
            testId: listeningTest.id,
            difficulty: listeningTest.difficulty,
            completed: false,
            score: null,
            required: true,
            isTest: true,
            order: 1,
            description: 'Listening comprehension test',
          });

          dayPlan.activities.push({
            id: `reading-test-week-${weekNumber}`,
            type: 'reading',
            name: readingTest.name,
            duration: 60,
            path: readingTest.path,
            testId: readingTest.id,
            difficulty: readingTest.difficulty,
            completed: false,
            score: null,
            required: true,
            isTest: true,
            order: 2,
            description: 'Reading comprehension test',
          });

          dayPlan.activities.push({
            id: `writing-test-week-${weekNumber}`,
            type: 'writing',
            name: writingTest.name,
            duration: 60,
            path: writingTest.path,
            testId: writingTest.id,
            difficulty: writingTest.difficulty,
            completed: false,
            score: null,
            required: true,
            isTest: true,
            order: 3,
            description: 'Writing skills test',
          });

          dayPlan.activities.push({
            id: `speaking-test-week-${weekNumber}`,
            type: 'speaking',
            name: 'Speaking Assessment',
            duration: 15,
            path: '/exams/english/speaking',
            topicId: speakingTopic.id,
            topic: speakingTopic.topic,
            category: speakingTopic.category,
            completed: false,
            score: null,
            required: true,
            isTest: true,
            order: 4,
            description: 'Speaking assessment',
          });

          dayPlan.lessonName = `Week ${weekNumber} Test`;
          dayPlan.lessonType = 'checkpoint';
          listeningTestIndex++;
          readingTestIndex++;
          writingTestIndex++;
        } else {
          dayPlan.activities.push({
            id: `revision-quiz-week-${weekNumber}`,
            type: 'revision-quiz',
            name: 'Weekly Review Quiz',
            duration: 30,
            path: '/exams/english/revision-session',
            completed: false,
            score: null,
            required: true,
            isTest: true,
            isRevisionCheckpoint: true,
            order: 1,
            description: 'Review this week’s topics',
          });
          dayPlan.lessonName = `Week ${weekNumber} Review`;
          dayPlan.lessonType = 'revision-checkpoint';
        }
      }
    }

    journey.push(dayPlan);
  }

  return journey;
};

/**
 * Calculate day completion percentage
 * @param {object} dayPlan - The day's plan with activities
 * @returns {object} - { percentage: number, status: 'completed' | 'partial' | 'pending', canProceedToNextDay: boolean }
 *
 * Logic for Regular Days (Mon-Sat):
 * - Speaking session completed = 50%
 * - Lesson/course completed = 50%
 * - Both = 100% (completed)
 * - Only one = 50% (partial - shown as yellow)
 * - None = 0% (pending)
 *
 * Progression Logic:
 * - User can proceed to next day if course/lesson is completed
 * - Speaking is optional for progression (useful when voice minutes are exhausted)
 * - Day shows as 'completed' only when both are done
 *
 * Logic for Weekly Test Days (Sunday):
 * - All 4 tests must be completed for 100%
 * - Each test = 25%
 * - Any tests completed = partial (yellow)
 */
export const calculateDayProgress = (dayPlan) => {
  // Check if this is a weekly test day (Sunday/checkpoint)
  if (dayPlan.isCheckpoint) {
    const testActivities = dayPlan.activities.filter((a) => a.isTest);
    const completedTests = testActivities.filter((a) => a.completed === true);

    const percentage = Math.round(
      (completedTests.length / testActivities.length) * 100
    );

    let status = 'pending';
    if (percentage === 100) {
      status = 'completed';
    } else if (percentage > 0) {
      status = 'partial';
    }

    // For checkpoints, can proceed if at least 3 of 4 tests are done (75%)
    // This allows some flexibility while still requiring substantial completion
    const canProceedToNextDay = completedTests.length >= 3;

    return {
      percentage,
      status,
      testsCompleted: completedTests.length,
      totalTests: testActivities.length,
      canProceedToNextDay,
    };
  }

  // Regular day logic (Mon-Sat)
  const speakingActivity = dayPlan.activities.find(
    (a) => a.type === 'speaking'
  );
  const courseActivity = dayPlan.activities.find(
    (a) =>
      a.type !== 'speaking' &&
      (a.type === 'grammar' ||
        a.type === 'vocabulary' ||
        a.type === 'writing-course' ||
        a.type === 'reading-course' ||
        a.type === 'listening-course' ||
        a.type === 'speaking-course')
  );

  let speakingDone = speakingActivity?.completed === true;
  let courseDone = courseActivity?.completed === true;

  // Calculate percentage
  let percentage = 0;
  if (speakingDone) percentage += 50;
  if (courseDone) percentage += 50;

  // Determine status
  let status = 'pending';
  if (percentage === 100) {
    status = 'completed';
  } else if (percentage > 0) {
    status = 'partial';
  }

  // User can proceed to next day if the course/lesson is completed
  // Speaking is optional for progression (e.g., when voice minutes are exhausted)
  const canProceedToNextDay = courseDone;

  return { percentage, status, speakingDone, courseDone, canProceedToNextDay };
};

export const weekIndexForDay = (dayNumber) =>
  Math.max(1, Math.floor((Number(dayNumber || 1) - 1) / 7) + 1);

export const getJourneyWeekDays = (journeyData, weekIndex) => {
  const days = journeyData?.journey || [];
  const start = (Number(weekIndex) - 1) * 7;
  return days.slice(start, start + 7);
};

export const isDayFullyCompleteForReward = (dayPlan) => {
  const activities = dayPlan?.activities || [];
  return activities.length > 0 && activities.every((activity) => activity.completed === true);
};

export const isWeekFullyComplete = (journeyData, weekIndex) => {
  const days = getJourneyWeekDays(journeyData, weekIndex);
  return days.length === 7 && days.every(isDayFullyCompleteForReward);
};

export const isWeekReviewCompleted = (journeyData, weekIndex) => {
  const reviews = journeyData?.weekReviews || {};
  const entry = reviews[String(weekIndex)] || reviews[weekIndex];
  return Boolean(entry?.completedAt);
};

export const markWeekReviewCompleted = (journeyData, weekIndex) => {
  const next = { ...(journeyData || {}) };
  const reviews = { ...(next.weekReviews || {}) };
  const key = String(weekIndex);
  reviews[key] = {
    ...(reviews[key] || {}),
    completedAt: reviews[key]?.completedAt || new Date().toISOString(),
  };
  next.weekReviews = reviews;
  return next;
};

export const getMissingWeekTasks = (journeyData, weekIndex) => {
  const missing = [];
  const dayLabels = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  getJourneyWeekDays(journeyData, weekIndex).forEach((day, offset) => {
    const dayNumber = day.day || (weekIndex - 1) * 7 + offset + 1;
    const dayOfWeek = day.dayOfWeek || ((dayNumber - 1) % 7) + 1;
    (day.activities || []).forEach((activity) => {
      if (activity.completed === true) return;
      missing.push({
        day: dayNumber,
        dayLabel: dayLabels[dayOfWeek - 1],
        activityId: activity.id,
        type: activity.type,
        name: activity.name || activity.type,
        isSpeaking: activity.type === 'speaking',
        isCheckpoint: Boolean(day.isCheckpoint),
        path: activity.path,
        lessonId: activity.lessonId,
        difficulty: activity.difficulty,
        activity,
      });
    });
  });
  if (!isWeekReviewCompleted(journeyData, weekIndex)) {
    missing.push({
      day: weekIndex * 7,
      dayLabel: 'Sunday',
      type: 'weekly-review',
      name: 'Weekly review',
      isSpeaking: false,
    });
  }
  return missing;
};

export const getCurrentJourneyWeek = (journeyData) =>
  weekIndexForDay(
    journeyData?.effectiveCurrentDay || journeyData?.currentDay || 1
  );

const deepCloneJourneyData = (journeyData) =>
  JSON.parse(JSON.stringify(journeyData || {}));

const clearActivityCompletionState = (activity) => {
  const resetActivity = { ...activity, completed: false };
  delete resetActivity.completedAt;
  delete resetActivity.score;
  delete resetActivity.testResults;
  return resetActivity;
};

const completeActivityState = (activity, completedAt) => ({
  ...activity,
  completed: true,
  completedAt,
});

export const recomputeJourneyStatuses = (
  journeyData,
  userEmail = null,
  forcedCurrentDay = null
) => {
  if (!journeyData?.journey || !Array.isArray(journeyData.journey)) {
    return journeyData;
  }

  const nextJourney = deepCloneJourneyData(journeyData);
  const totalDays = nextJourney.journey.length || 1;
  const safeCurrentDay = Math.min(
    Math.max(Number(forcedCurrentDay || nextJourney.currentDay || 1), 1),
    totalDays
  );

  let highestProgressionDay = 0;

  nextJourney.journey = nextJourney.journey.map((day, index) => {
    const dayProgress = calculateDayProgress(day);
    if (dayProgress.status === 'completed' || dayProgress.canProceedToNextDay) {
      highestProgressionDay = Math.max(highestProgressionDay, index + 1);
    }

    return {
      ...day,
      progress: dayProgress,
    };
  });

  const effectiveCurrentDay = Math.min(
    Math.max(safeCurrentDay, highestProgressionDay + 1),
    totalDays
  );

  nextJourney.journey = nextJourney.journey.map((day, index) => {
    const dayNumber = index + 1;
    const dayProgress = day.progress || calculateDayProgress(day);
    let status = day.status;

    if (dayNumber < effectiveCurrentDay) {
      status = dayProgress.status === 'pending' ? 'missed' : dayProgress.status;
    } else if (dayNumber === effectiveCurrentDay) {
      status =
        dayProgress.status === 'completed'
          ? 'completed'
          : dayProgress.status === 'partial'
            ? 'partial'
            : 'current';
    } else if (dayNumber <= effectiveCurrentDay + 2) {
      status =
        dayProgress.status === 'completed'
          ? 'completed'
          : dayProgress.status === 'partial'
            ? 'partial'
            : 'available';
    } else {
      status = 'locked';
    }

    return {
      ...day,
      status,
      progress: dayProgress,
    };
  });

  nextJourney.currentDay = safeCurrentDay;
  nextJourney.effectiveCurrentDay = effectiveCurrentDay;
  nextJourney.lastUpdated = new Date().toISOString();

  return nextJourney;
};

export const setJourneyToDay = (
  journeyData,
  targetDay,
  { adminEmail = null } = {}
) => {
  if (!journeyData?.journey || !Array.isArray(journeyData.journey)) {
    return journeyData;
  }

  const nextJourney = deepCloneJourneyData(journeyData);
  const totalDays = nextJourney.journey.length || 1;
  const safeTargetDay = Math.min(
    Math.max(Number(targetDay || 1), 1),
    totalDays
  );
  const completedAt = new Date().toISOString();

  nextJourney.journey = nextJourney.journey.map((day, index) => {
    const shouldCompleteDay = index + 1 < safeTargetDay;

    return {
      ...day,
      activities: (day.activities || []).map((activity) =>
        shouldCompleteDay
          ? completeActivityState(activity, completedAt)
          : clearActivityCompletionState(activity)
      ),
    };
  });

  nextJourney.manualAdminUpdateAt = completedAt;
  nextJourney.manualAdminUpdatedBy = adminEmail;
  nextJourney.manualAdminTargetDay = safeTargetDay;

  return recomputeJourneyStatuses(nextJourney, null, safeTargetDay);
};

export const markJourneyDayCompleted = (
  journeyData,
  dayNumber,
  completed = true,
  { adminEmail = null } = {}
) => {
  if (!journeyData?.journey || !Array.isArray(journeyData.journey)) {
    return journeyData;
  }

  const nextJourney = deepCloneJourneyData(journeyData);
  const totalDays = nextJourney.journey.length || 1;
  const safeDayNumber = Math.min(
    Math.max(Number(dayNumber || 1), 1),
    totalDays
  );
  const completedAt = new Date().toISOString();

  nextJourney.journey = nextJourney.journey.map((day, index) => {
    if (index + 1 !== safeDayNumber) return day;

    return {
      ...day,
      activities: (day.activities || []).map((activity) =>
        completed
          ? completeActivityState(activity, completedAt)
          : clearActivityCompletionState(activity)
      ),
    };
  });

  nextJourney.manualAdminUpdateAt = completedAt;
  nextJourney.manualAdminUpdatedBy = adminEmail;
  nextJourney.manualAdminTargetDay = completed
    ? Math.min(safeDayNumber + 1, totalDays)
    : safeDayNumber;

  return recomputeJourneyStatuses(
    nextJourney,
    null,
    nextJourney.manualAdminTargetDay
  );
};

export const getJourneyAdminSummary = (journeyData) => {
  if (!journeyData?.journey || !Array.isArray(journeyData.journey)) {
    return null;
  }

  const totalDays = journeyData.journey.length || 0;
  const completedDays = journeyData.journey.filter((day) =>
    (day.activities || []).every((activity) => activity.completed === true)
  ).length;
  const completedActivities = journeyData.journey.reduce(
    (total, day) =>
      total +
      (day.activities || []).filter((activity) => activity.completed).length,
    0
  );
  const totalActivities = journeyData.journey.reduce(
    (total, day) => total + (day.activities || []).length,
    0
  );

  return {
    currentLevel: journeyData.currentLevel,
    targetLevel: journeyData.targetLevel,
    totalDays,
    completedDays,
    currentDay:
      journeyData.manualAdminTargetDay ||
      journeyData.effectiveCurrentDay ||
      journeyData.currentDay ||
      1,
    completedActivities,
    totalActivities,
    manualAdminUpdateAt: journeyData.manualAdminUpdateAt || null,
    manualAdminUpdatedBy: journeyData.manualAdminUpdatedBy || null,
  };
};

/**
 * Validate if a day is complete based on criteria
 * Day is complete only if:
 * 1. Speaking session is completed
 * 2. Course/test score is >= 80%
 */
export const validateDayCompletion = (dayPlan) => {
  const { status } = calculateDayProgress(dayPlan);
  return status === 'completed';
};

export const activityDurationMinutes = (activity) => {
  if (!activity) return 0;
  if (activity.type === 'speaking' && !activity.isTest) {
    return getSpeakingPracticeMinutes();
  }
  const stored = Number(activity.duration);
  return Number.isFinite(stored) ? stored : 0;
};

export const IELTS_PRACTICE_LABELS = {
  speaking: 'Speaking',
  'speaking-course': 'Speaking',
  listening: 'Listening',
  'listening-course': 'Listening',
  reading: 'Reading',
  'reading-course': 'Reading',
  writing: 'Writing',
  'writing-course': 'Writing',
  grammar: 'Grammar',
  vocabulary: 'Vocabulary',
  'revision-session': 'Review',
  'revision-quiz': 'Review',
  'weekly-review': 'Review',
  mock: 'Full mock',
};

export const IELTS_WEEK_STRUCTURE = [
  { day: 'Mon', label: 'Grammar + Speaking' },
  { day: 'Tue', label: 'Vocabulary + Speaking' },
  { day: 'Wed', label: 'Writing + Speaking' },
  { day: 'Thu', label: 'Reading + Speaking' },
  { day: 'Fri', label: 'Listening + Speaking' },
  { day: 'Sat', label: 'Speaking practice' },
  { day: 'Sun', label: 'Listening, Reading, Writing, Speaking' },
];

const IELTS_SKILL_ORDER = ['Listening', 'Reading', 'Writing', 'Speaking'];

export const ieltsPracticeLabel = (type) => IELTS_PRACTICE_LABELS[type] || '';

const titleAlreadyNamesSkill = (name, skill) =>
  Boolean(name && skill && name.toLowerCase().includes(skill.toLowerCase()));

export const formatIeltsActivityTitle = (activity) => {
  const skill = ieltsPracticeLabel(activity?.type);
  const name = String(activity?.name || '').trim();
  if (!skill) return name || 'English practice';
  if (!name || titleAlreadyNamesSkill(name, skill)) return name || skill;
  return `${skill}: ${name}`;
};

export const formatIeltsActivityKind = (activity) => {
  if (!activity) return 'practice';
  if (activity.isTest || activity.type === 'mock') return 'test';
  if (activity.type === 'grammar' || activity.type === 'vocabulary') return 'lesson';
  if (
    activity.type === 'revision-session' ||
    activity.type === 'revision-quiz' ||
    activity.type === 'weekly-review'
  ) {
    return 'review';
  }
  return 'practice';
};

export const formatIeltsActivitySublabel = (activity) => {
  const minutes = activityDurationMinutes(activity);
  const skill = ieltsPracticeLabel(activity?.type);
  const kind = formatIeltsActivityKind(activity);
  const parts = [];
  if (minutes) parts.push(`${minutes} min`);
  if (skill) parts.push(`${skill} ${kind}`);
  return parts.join(' · ');
};

export const formatIeltsHomeTask = (activity) => {
  const isDailySpeaking = activity?.type === 'speaking' && !activity?.isTest;
  const label =
    isDailySpeaking && activity.category
      ? `Speaking: ${activity.category}`
      : formatIeltsActivityTitle(activity);
  return {
    label,
    sublabel: formatIeltsActivitySublabel(activity),
  };
};

export const formatIeltsDayProgramTitle = (day) => {
  const activities = day?.activities || [];
  const isCheckpoint =
    Boolean(day?.isCheckpoint) ||
    ['checkpoint', 'mock-checkpoint', 'revision-checkpoint'].includes(day?.lessonType);

  if (isCheckpoint) {
    if (activities.some((activity) => activity.type === 'mock') || day?.lessonType === 'mock-checkpoint') {
      return 'Full mock: Listening, Reading, Writing, Speaking';
    }
    if (
      activities.some((activity) => activity.type === 'revision-quiz') ||
      day?.lessonType === 'revision-checkpoint'
    ) {
      return 'Weekly review';
    }
    const skills = IELTS_SKILL_ORDER.filter((skill) =>
      activities.some((activity) => ieltsPracticeLabel(activity.type) === skill)
    );
    return skills.length ? skills.join(', ') : 'Weekly test';
  }

  const lesson = activities.find((activity) => activity.type !== 'speaking') || activities[0];
  return formatIeltsActivityTitle(lesson);
};

export const formatIeltsCheckpointNodeLabel = (day) => {
  const week = day?.weekNumber ? `Week ${day.weekNumber}` : 'Week';
  const title = formatIeltsDayProgramTitle(day);
  if (title === 'Weekly review') {
    return { title: `${week} review`, detail: 'Grammar & vocab' };
  }
  if (title.startsWith('Full mock')) {
    return { title: `${week} mock`, detail: 'L · R · W · S' };
  }
  return { title: `${week} test`, detail: 'L · R · W · S' };
};

/**
 * Get the current day's activities based on user progress
 * @param {Array} journey - The journey plan array
 * @param {number} currentDay - Current day in the journey (1-60)
 * @returns {object} Today's activities and metadata
 */
export const getTodayActivities = (journey, currentDay) => {
  const today = journey.find((d) => d.day === currentDay);
  if (!today) {
    return null;
  }

  const activities = (today.activities || []).map((activity) => {
    const duration = activityDurationMinutes(activity);
    return duration === activity.duration ? activity : { ...activity, duration };
  });

  return {
    ...today,
    activities,
    totalDuration: activities.reduce((sum, activity) => sum + activity.duration, 0),
    completedCount: today.activities.filter((a) => a.completed).length,
    totalCount: today.activities.length,
  };
};

/**
 * Calculate overall journey progress
 * @param {Array} journey - The journey plan array
 * @returns {object} Progress statistics
 */
export const getJourneyProgress = (journey) => {
  const completedDays = journey.filter((d) => d.status === 'completed').length;
  const totalDays = journey.length;
  const completedActivities = journey.reduce(
    (sum, d) => sum + d.activities.filter((a) => a.completed).length,
    0
  );
  const totalActivities = journey.reduce(
    (sum, d) => sum + d.activities.length,
    0
  );

  return {
    completedDays,
    totalDays,
    daysProgress: Math.round((completedDays / totalDays) * 100),
    completedActivities,
    totalActivities,
    activitiesProgress: Math.round(
      (completedActivities / totalActivities) * 100
    ),
    currentWeek: Math.ceil((completedDays + 1) / 7),
    totalWeeks: Math.ceil(totalDays / 7),
  };
};

const findSpeakingTopicById = (collection, topicId) =>
  collection.find((topic) => String(topic.id) === String(topicId));

const hydrateSpeakingJourneyActivity = (activity) => {
  if (!activity || activity.type !== 'speaking' || !activity.topicId) {
    return activity;
  }

  if (
    activity.vocabulary ||
    activity.opinionPrompt ||
    activity.infoContent ||
    activity.cueCard
  ) {
    return activity;
  }

  let sourceTopic = null;
  if (activity.speakingType === 'describe') {
    sourceTopic = findSpeakingTopicById(
      structuredTopicsData.describeExplain || [],
      activity.topicId
    );
  } else if (activity.speakingType === 'opinion') {
    sourceTopic = findSpeakingTopicById(
      structuredTopicsData.opinion || [],
      activity.topicId
    );
  } else if (activity.speakingType === 'info-based') {
    sourceTopic = findSpeakingTopicById(
      structuredTopicsData.infoBased || [],
      activity.topicId
    );
  } else {
    sourceTopic = findSpeakingTopicById(speakingTopicsData || [], activity.topicId);
  }

  if (!sourceTopic) {
    return activity;
  }

  return {
    ...activity,
    topic: activity.topic || sourceTopic.topic,
    category: activity.category || sourceTopic.category,
    vocabulary: activity.vocabulary || sourceTopic.vocabulary,
    cueCard: activity.cueCard || sourceTopic.cueCard || null,
    opinionPrompt: activity.opinionPrompt || sourceTopic.opinionPrompt || null,
    infoContent: activity.infoContent || sourceTopic.infoContent || null,
  };
};

const hydrateJourneyDataForRuntime = (journeyData) => {
  if (!journeyData?.journey || !Array.isArray(journeyData.journey)) {
    return journeyData;
  }

  let changed = false;
  const hydratedJourney = journeyData.journey.map((day) => {
    if (!Array.isArray(day.activities)) {
      return day;
    }

    let dayChanged = false;
    const hydratedActivities = day.activities.map((activity) => {
      const hydratedActivity = hydrateSpeakingJourneyActivity(activity);
      if (hydratedActivity !== activity) {
        dayChanged = true;
        changed = true;
      }
      return hydratedActivity;
    });

    if (!dayChanged) {
      return day;
    }

    return {
      ...day,
      activities: hydratedActivities,
    };
  });

  return changed
    ? {
        ...journeyData,
        journey: hydratedJourney,
      }
    : journeyData;
};

/**
 * Save journey progress to localStorage and sync to backend
 * @param {string} userEmail - User's email
 * @param {object} journeyData - Journey data to save
 */
export const saveJourneyProgress = async (userEmail, journeyData) => {
  const key = `languageJourney_${userEmail}`;
  const dataToSave = JSON.stringify({
    ...journeyData,
    version: JOURNEY_VERSION,
    lastUpdated: new Date().toISOString(),
  });

  // Save to localStorage first
  localStorage.setItem(key, dataToSave);
  if (journeyData?.enrolled) {
    invalidateHomeMandatorySnapshot(userEmail);
  }

  // Force immediate sync to backend (don't rely on debounce)
  try {
    console.log('Syncing journey progress to backend...');
    await syncLocalStorageToBackend(userEmail, true); // Force sync
    console.log('Journey progress synced to backend successfully');
  } catch (error) {
    console.error('Error syncing journey to backend:', error);
    // Still save locally even if sync fails
  }
};

const activityMatchKey = (activity) => {
  if (activity?.lessonId) return `lesson:${activity.lessonId}`;
  if (activity?.testId) return `test:${activity.type}:${activity.testId}`;
  if (activity?.topicId) return `topic:${activity.topicId}`;
  if (activity?.mockExamId) return `mock:${activity.mockExamId}`;
  return `type:${activity?.type || 'unknown'}`;
};

export const mergeJourneyCompletions = (oldJourney = [], newJourney = []) => {
  const completedByKey = new Map();
  const fallbackPool = [];

  oldJourney.forEach((day) => {
    (day.activities || []).forEach((activity) => {
      if (!activity?.completed) return;
      completedByKey.set(activityMatchKey(activity), activity);
      fallbackPool.push({
        type: activity.type,
        dayOfWeek: day.dayOfWeek,
        activity,
      });
    });
  });

  const used = new Set();

  newJourney.forEach((day) => {
    (day.activities || []).forEach((activity) => {
      const key = activityMatchKey(activity);
      let oldActivity = completedByKey.get(key);
      if (!oldActivity) {
        const fallback = fallbackPool.find(
          (item) =>
            item.type === activity.type &&
            item.dayOfWeek === day.dayOfWeek &&
            !used.has(item.activity)
        );
        oldActivity = fallback?.activity;
      }
      if (!oldActivity) return;
      used.add(oldActivity);
      activity.completed = true;
      activity.completedAt = oldActivity.completedAt;
      if (oldActivity.score != null) activity.score = oldActivity.score;
      if (oldActivity.testResults) activity.testResults = oldActivity.testResults;
    });

    const completedActivities =
      day.activities?.filter((activity) => activity.completed).length || 0;
    const totalActivities = day.activities?.length || 1;
    if (completedActivities === totalActivities) {
      day.status = 'completed';
    } else if (completedActivities > 0) {
      day.status = 'in_progress';
    }
  });

  const firstIncompleteDayIndex = newJourney.findIndex(
    (day) => day.status !== 'completed'
  );
  if (firstIncompleteDayIndex >= 0) {
    newJourney[firstIncompleteDayIndex].status = 'current';
  } else if (newJourney.length > 0) {
    newJourney[0].status = 'current';
  }

  return newJourney;
};

const LESSON_COMPLETION_TYPES = [
  'writing-course',
  'reading-course',
  'listening-course',
  'speaking-course',
  'revision-session',
  'grammar',
  'vocabulary',
  'reading',
  'writing',
  'listening',
  'speaking',
];

export const parseLessonCompletionKey = (key) => {
  const type = LESSON_COMPLETION_TYPES.find((item) =>
    String(key || '').startsWith(`${item}-`)
  );
  if (!type) return null;
  const id = String(key).slice(type.length + 1);
  if (!id) return null;
  return { type, id };
};

const journeyDayIndexForCompletion = (
  startDate,
  completedAt,
  totalDays,
  holdIntervals = []
) => {
  if (!startDate || !completedAt) return -1;
  const start = new Date(startDate);
  const done = new Date(completedAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(done.getTime())) return -1;
  start.setHours(0, 0, 0, 0);
  done.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((done - start) / (1000 * 60 * 60 * 24)) + 1;
  const pausedDays = countHoldDaysOverlapping(start, done, holdIntervals);
  const dayNumber = diffDays - pausedDays;
  if (dayNumber < 1 || dayNumber > totalDays) return -1;
  return dayNumber - 1;
};

const readStoredLessonCompletions = (userEmail) => {
  const parse = (raw) => {
    if (!raw) return {};
    try {
      const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return value && typeof value === 'object' ? value : {};
    } catch {
      return {};
    }
  };
  const scopedKey = getAccountScopedLocalStorageKey(
    'lesson_completion_data',
    userEmail
  );
  return {
    ...parse(localStorage.getItem('lesson_completion_data')),
    ...parse(scopedKey ? localStorage.getItem(scopedKey) : null),
  };
};

/**
 * Put completed reading, writing and listening work back on the day it was done.
 * lesson_completion_data survives the journey rebuild; the day checklist does not.
 */
export const restoreJourneyFromLessonCompletions = (
  journeyData,
  lessonCompletions,
  holdIntervals = []
) => {
  if (!journeyData?.journey || !lessonCompletions) {
    return { journeyData, changed: false };
  }

  const journey = journeyData.journey.map((day) => ({
    ...day,
    activities: (day.activities || []).map((activity) => ({ ...activity })),
  }));
  let changed = false;

  const markCompleted = (activity, completedAt) => {
    if (!activity || activity.completed === true) return false;
    activity.completed = true;
    if (completedAt) activity.completedAt = completedAt;
    changed = true;
    return true;
  };

  Object.entries(lessonCompletions).forEach(([key, entry]) => {
    const parsed = parseLessonCompletionKey(key);
    if (!parsed || !entry) return;
    const completedAt =
      entry && typeof entry === 'object' ? entry.completedAt : null;
    const dayIndex = journeyDayIndexForCompletion(
      journeyData.startDate,
      completedAt,
      journey.length,
      holdIntervals
    );

    if (dayIndex >= 0) {
      const sameDay = journey[dayIndex].activities.find(
        (activity) => activity.type === parsed.type && activity.completed !== true
      );
      if (markCompleted(sameDay, completedAt)) return;
    }

    const exact = journey
      .flatMap((day) => day.activities)
      .find((activity) => {
        if (activity.type !== parsed.type || activity.completed === true) {
          return false;
        }
        if (
          activity.lessonId != null &&
          String(activity.lessonId) === parsed.id
        ) {
          return true;
        }
        return (
          activity.testId != null && String(activity.testId) === String(parsed.id)
        );
      });
    markCompleted(exact, completedAt);
  });

  if (!changed) return { journeyData, changed: false };
  return { journeyData: { ...journeyData, journey }, changed: true };
};

const finishJourneyLoad = (journeyData, userEmail, shouldSync = false) => {
  if (!journeyData || !userEmail) return journeyData;
  const { journeyData: restored, changed } = restoreJourneyFromLessonCompletions(
    journeyData,
    readStoredLessonCompletions(userEmail),
    getStoredHoldIntervals()
  );
  const nextJourney = changed ? restored : journeyData;
  if (shouldSync || changed) {
    saveJourneyProgress(userEmail, nextJourney).catch((error) => {
      console.error('Failed to save restored journey progress:', error);
    });
  }
  return nextJourney;
};

/**
 * Load journey progress from localStorage
 * Checks version and regenerates if outdated
 * @param {string} userEmail - User's email
 * @returns {object|null} Saved journey data or null
 */
export const loadJourneyProgress = (userEmail) => {
  const key = `languageJourney_${userEmail}`;
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);

      // Check if journey is outdated and needs regeneration
      if (
        parsed.enrolled &&
        (!parsed.version || parsed.version < JOURNEY_VERSION)
      ) {
        console.log(
          `Journey version mismatch: ${parsed.version || 'none'} < ${JOURNEY_VERSION}. Regenerating...`
        );

        // Count completed activities BEFORE regeneration
        const oldCompletedCount =
          parsed.journey?.reduce((total, day) => {
            return (
              total +
              (day.activities?.filter((a) => a.completed === true).length || 0)
            );
          }, 0) || 0;

        console.log(
          `Old journey had ${oldCompletedCount} completed activities`
        );

        // Regenerate journey while preserving user's enrollment and progress
        // Pass userEmail to ensure deterministic journey generation
        const newJourney = generateJourneyPlan(
          parsed.currentLevel || 'B1',
          parsed.targetLevel || 'C1',
          new Date(parsed.startDate),
          userEmail
        );

        if (parsed.journey && oldCompletedCount > 0) {
          mergeJourneyCompletions(parsed.journey, newJourney);
        }

        const updatedData = {
          ...parsed,
          journey: newJourney,
          version: JOURNEY_VERSION,
          lastUpdated: new Date().toISOString(),
        };

        // Save the regenerated journey
        localStorage.setItem(key, JSON.stringify(updatedData));
        console.log('Journey regenerated successfully with preserved progress');

        return updatedData;
      }

      const hydratedParsed = hydrateJourneyDataForRuntime(parsed);
      if (hydratedParsed !== parsed) {
        localStorage.setItem(key, JSON.stringify(hydratedParsed));
      }

      return hydratedParsed;
    } catch (e) {
      console.error('Error parsing journey data:', e);
      return null;
    }
  }
  return null;
};

/**
 * Helper function to fetch with retry logic
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in ms (doubles each retry)
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithRetry = async (
  url,
  options,
  maxRetries = 3,
  baseDelay = 500
) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // Don't retry on client errors (4xx), only on server errors (5xx) or network issues
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }

      // Server error - will retry
      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
      console.warn(
        `Backend request failed (attempt ${attempt + 1}/${maxRetries + 1}):`,
        lastError.message
      );
    } catch (error) {
      // Network error - will retry
      lastError = error;
      console.warn(
        `Network error (attempt ${attempt + 1}/${maxRetries + 1}):`,
        error.message
      );
    }

    // Don't delay after the last attempt
    if (attempt < maxRetries) {
      const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff: 500, 1000, 2000ms
      console.log(`Retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

/**
 * Load journey progress from backend
 * Also loads lesson_completion_data if available
 * Includes retry logic for transient failures
 * @param {string} userEmail - User's email
 * @param {number} maxRetries - Maximum number of retries (default: 2)
 * @returns {Promise<object|null>} Journey data from backend or null
 */
export const loadJourneyFromBackend = async (userEmail, maxRetries = 2) => {
  if (!userEmail) return null;

  try {
    console.log('Fetching journey from backend for:', userEmail);
    const configuration = await fetchBackendConfiguration(userEmail, {
      maxRetries,
      scope: 'journey',
    });

    if (!configuration) return null;

    const voiceKey = getVoiceLimitsStorageKey(userEmail);
    if (voiceKey && configuration[voiceKey] != null) {
      const voiceLimits = configuration[voiceKey];
      localStorage.setItem(
        voiceKey,
        typeof voiceLimits === 'string' ? voiceLimits : JSON.stringify(voiceLimits)
      );
    }

    const key = `languageJourney_${userEmail}`;
    console.log('Looking for key:', key);
    console.log('Available keys in configuration:', Object.keys(configuration));

    // Also load lesson_completion_data from backend if available.
    // Store it in the account-scoped key so lesson state cannot leak accounts.
    const lessonCompletionKey = getAccountScopedLocalStorageKey(
      'lesson_completion_data',
      userEmail
    );
    const lessonCompletionData =
      configuration[lessonCompletionKey] ||
      configuration['lesson_completion_data'];
    if (lessonCompletionData) {
      const localLessonData = lessonCompletionKey
        ? localStorage.getItem(lessonCompletionKey)
        : null;
      const backendData =
        typeof lessonCompletionData === 'string'
          ? lessonCompletionData
          : JSON.stringify(lessonCompletionData);

      // Merge backend lesson data with local (take the union of completed lessons)
      try {
        const localParsed = JSON.parse(localLessonData || '{}');
        const backendParsed =
          typeof lessonCompletionData === 'string'
            ? JSON.parse(lessonCompletionData)
            : lessonCompletionData;

        const merged = { ...localParsed, ...backendParsed };
        if (lessonCompletionKey) {
          localStorage.setItem(lessonCompletionKey, JSON.stringify(merged));
        }
        console.log('Merged lesson_completion_data from backend');
      } catch (e) {
        // If parsing fails, just use backend data
        if (lessonCompletionKey) {
          localStorage.setItem(lessonCompletionKey, backendData);
        }
        console.log('Loaded lesson_completion_data from backend');
      }
    }

    let journeyData = configuration[key];

    // Also try without the email suffix in case the key is stored differently
    if (!journeyData) {
      // Look for any languageJourney key
      const journeyKey = Object.keys(configuration).find((k) =>
        k.startsWith('languageJourney_')
      );
      if (journeyKey) {
        console.log('Found journey with alternative key:', journeyKey);
        journeyData = configuration[journeyKey];
      }
    }

    if (!journeyData) {
      console.log('No journey data found in backend for this user');
      return null;
    }

    console.log('Journey found in backend configuration');

    // Parse if it's a string
    let parsed;
    try {
      parsed =
        typeof journeyData === 'string' ? JSON.parse(journeyData) : journeyData;
    } catch (parseError) {
      console.error('Error parsing journey data:', parseError);
      return null;
    }

    // Validate the parsed data has required fields
    if (parsed && parsed.enrolled !== undefined) {
      const hydratedParsed = hydrateJourneyDataForRuntime(parsed);

      // Save to localStorage for future use
      localStorage.setItem(key, JSON.stringify(hydratedParsed));
      console.log(
        'Journey saved to localStorage from backend. Enrolled:',
        hydratedParsed.enrolled,
        'Days completed:',
        hydratedParsed.journey?.filter((d) => d.status === 'completed').length ||
          0
      );
      return hydratedParsed;
    }

    console.warn('Journey data missing required fields:', parsed);
    return null;
  } catch (error) {
    console.error('Error loading journey from backend:', error);
    return null;
  }
};

/**
 * Check if two journeys are the same (same enrollment/identity)
 * Two journeys are the same if they have the same startDate and levels
 * @param {object} journey1 - First journey
 * @param {object} journey2 - Second journey
 * @returns {boolean} - true if same journey
 */
const isSameJourney = (journey1, journey2) => {
  if (!journey1 || !journey2) return false;

  // Compare by startDate (most reliable identifier)
  const startDate1 = journey1.startDate
    ? new Date(journey1.startDate).toISOString().split('T')[0]
    : null;
  const startDate2 = journey2.startDate
    ? new Date(journey2.startDate).toISOString().split('T')[0]
    : null;

  // If start dates match and levels match, it's the same journey
  return (
    startDate1 === startDate2 &&
    journey1.currentLevel === journey2.currentLevel &&
    journey1.targetLevel === journey2.targetLevel
  );
};

const shouldPreferIntentionalJourneyReset = (localJourney, backendJourney) => {
  if (!localJourney?.levelUpdatedFromTest || !backendJourney) return false;

  const localStartTime = new Date(localJourney.startDate || 0).getTime();
  const backendStartTime = new Date(backendJourney.startDate || 0).getTime();
  const hasJourneyChanged =
    localJourney.currentLevel !== backendJourney.currentLevel ||
    localJourney.targetLevel !== backendJourney.targetLevel ||
    localStartTime !== backendStartTime;

  return hasJourneyChanged && localStartTime >= backendStartTime;
};

/**
 * Merge two journeys that are the same (combine completed activities)
 * @param {object} journey1 - First journey
 * @param {object} journey2 - Second journey
 * @returns {object} - Merged journey with all completed activities from both
 */
const mergeJourneyProgress = (journey1, journey2) => {
  // Use the more recently updated one as base
  const j1Time = new Date(journey1.lastUpdated || 0).getTime();
  const j2Time = new Date(journey2.lastUpdated || 0).getTime();
  const base = j2Time > j1Time ? { ...journey2 } : { ...journey1 };
  const other = j2Time > j1Time ? journey1 : journey2;

  // Deep clone the journey array
  base.journey = JSON.parse(JSON.stringify(base.journey || []));

  // Merge completed activities from both journeys
  if (other.journey && base.journey) {
    other.journey.forEach((otherDay, dayIndex) => {
      if (base.journey[dayIndex] && otherDay.activities) {
        otherDay.activities.forEach((otherActivity) => {
          if (otherActivity.completed) {
            // Find matching activity in base by type
            const baseActivity = base.journey[dayIndex].activities?.find(
              (a) => a.type === otherActivity.type
            );
            if (baseActivity && !baseActivity.completed) {
              // Copy completed status from other
              baseActivity.completed = true;
              baseActivity.completedAt = otherActivity.completedAt;
              if (otherActivity.score) baseActivity.score = otherActivity.score;
              if (otherActivity.testResults)
                baseActivity.testResults = otherActivity.testResults;
              console.log(
                `📋 Merged completion: Day ${dayIndex + 1} - ${otherActivity.type}`
              );
            }
          }
        });
      }
    });
  }

  const baseReviews = base.weekReviews || {};
  const otherReviews = other.weekReviews || {};
  base.weekReviews = { ...otherReviews, ...baseReviews };

  // Update lastUpdated to now since we merged
  base.lastUpdated = new Date().toISOString();

  return base;
};

/**
 * Load journey progress - fetches from both localStorage and backend
 * Uses smart comparison: same journey = merge progress, different journey = use original
 * @param {string} userEmail - User's email
 * @returns {Promise<object|null>} Journey data or null
 */
export const loadJourneyProgressAsync = async (userEmail) => {
  const key = `languageJourney_${userEmail}`;
  await loadJourneyHoldIntervals();

  // Load from both sources in parallel
  const localJourney = loadJourneyProgress(userEmail);
  const backendJourney = await loadJourneyFromBackend(userEmail);

  console.log('Local journey exists:', !!localJourney);
  console.log('Backend journey exists:', !!backendJourney);

  // If neither exists, return null
  if (!localJourney && !backendJourney) {
    return null;
  }

  // If only one exists, use that one
  if (!localJourney && backendJourney) {
    console.log('Using backend journey (no local data)');
    const regenerated =
      !backendJourney.version || backendJourney.version < JOURNEY_VERSION;
    return finishJourneyLoad(
      processJourneyData(backendJourney, key, userEmail),
      userEmail,
      regenerated
    );
  }

  if (localJourney && !backendJourney) {
    console.log('Using local journey (no backend data)');
    return finishJourneyLoad(localJourney, userEmail, true);
  }

  // Both exist - check if they are the SAME journey (same enrollment)
  const sameJourney = isSameJourney(localJourney, backendJourney);

  console.log('Local startDate:', localJourney.startDate);
  console.log('Backend startDate:', backendJourney.startDate);
  console.log('Same journey?', sameJourney);

  if (sameJourney) {
    // SAME JOURNEY: Merge progress from both (union of completed activities)
    console.log(
      '✅ Same journey detected - merging progress from both sources'
    );

    const localCompletedCount = countCompletedActivities(localJourney);
    const backendCompletedCount = countCompletedActivities(backendJourney);

    console.log('Local completed activities:', localCompletedCount);
    console.log('Backend completed activities:', backendCompletedCount);

    // Merge both journeys to preserve all progress
    const mergedJourney = mergeJourneyProgress(localJourney, backendJourney);
    const mergedCompletedCount = countCompletedActivities(mergedJourney);

    console.log('Merged completed activities:', mergedCompletedCount);

    // Save merged result to localStorage
    localStorage.setItem(key, JSON.stringify(mergedJourney));

    return finishJourneyLoad(
      processJourneyData(mergedJourney, key, userEmail),
      userEmail,
      mergedCompletedCount > backendCompletedCount
    );
  } else {
    // DIFFERENT JOURNEYS: This is a conflict!
    // Use the OLDER one (original enrollment) as source of truth
    const localStartTime = new Date(localJourney.startDate || 0).getTime();
    const backendStartTime = new Date(backendJourney.startDate || 0).getTime();

    console.log('⚠️ Different journeys detected - conflict resolution needed');
    console.log('Local journey started:', localJourney.startDate);
    console.log('Backend journey started:', backendJourney.startDate);

    let useBackend = false;

    if (shouldPreferIntentionalJourneyReset(localJourney, backendJourney)) {
      console.log(
        '🆙 Keeping local intentional journey reset and syncing it to backend'
      );
      useBackend = false;
    } else if (backendStartTime > 0 && localStartTime > 0) {
      // Use the OLDER one (first enrollment is the real one)
      useBackend = backendStartTime < localStartTime;
      console.log(
        useBackend
          ? '📅 Using backend (older/original journey)'
          : '📅 Using local (older/original journey)'
      );
    } else {
      // One has no startDate - use the one with more progress
      const localCompletedCount = countCompletedActivities(localJourney);
      const backendCompletedCount = countCompletedActivities(backendJourney);
      useBackend = backendCompletedCount > localCompletedCount;
      console.log(
        useBackend
          ? '📊 Using backend (more progress)'
          : '📊 Using local (more progress)'
      );
    }

    if (useBackend) {
      localStorage.setItem(key, JSON.stringify(backendJourney));
      return finishJourneyLoad(
        processJourneyData(backendJourney, key, userEmail),
        userEmail,
        false
      );
    }

    // Sync local to backend to resolve the conflict
    console.log('🔄 Syncing local journey to backend to resolve conflict...');
    return finishJourneyLoad(localJourney, userEmail, true);
  }
};

/**
 * Count total completed activities in a journey
 */
const countCompletedActivities = (journeyData) => {
  if (!journeyData?.journey) return 0;
  return journeyData.journey.reduce((total, day) => {
    return (
      total + (day.activities?.filter((a) => a.completed === true).length || 0)
    );
  }, 0);
};

/**
 * Process journey data (handle version migration while preserving progress)
 * @param {object} journeyData - Journey data to process
 * @param {string} key - localStorage key
 * @param {string} userEmail - User's email for deterministic regeneration
 */
const processJourneyData = (journeyData, key, userEmail = null) => {
  // Extract userEmail from key if not provided
  const email = userEmail || key.replace('languageJourney_', '');

  // Check if journey is outdated and needs regeneration
  if (
    journeyData.enrolled &&
    (!journeyData.version || journeyData.version < JOURNEY_VERSION)
  ) {
    console.log(
      `Journey version mismatch: ${journeyData.version || 'none'} < ${JOURNEY_VERSION}. Regenerating...`
    );

    // Count completed activities BEFORE regeneration
    const oldCompletedCount = countCompletedActivities(journeyData);
    console.log(`Old journey had ${oldCompletedCount} completed activities`);

    // Regenerate journey while preserving user's enrollment and progress
    // Pass userEmail to ensure deterministic journey generation
    const newJourney = generateJourneyPlan(
      journeyData.currentLevel || 'B1',
      journeyData.targetLevel || 'C1',
      new Date(journeyData.startDate),
      email
    );

    if (journeyData.journey && oldCompletedCount > 0) {
      mergeJourneyCompletions(journeyData.journey, newJourney);
    }

    const updatedData = {
      ...journeyData,
      journey: newJourney,
      version: JOURNEY_VERSION,
      lastUpdated: new Date().toISOString(),
    };

    // Save the regenerated journey
    localStorage.setItem(key, JSON.stringify(updatedData));
    console.log('Journey regenerated with preserved progress');

    return updatedData;
  }

  return journeyData;
};

/**
 * Check if today's activities should unlock based on the calendar
 * @param {string} journeyStartDate - ISO date string of journey start
 * @param {string} userEmail - Optional user email to check for day override
 * @param {number} totalDays - Total days in the journey (default 60, supports multi-level journeys)
 * @returns {number} Current day number in the journey (1 to totalDays)
 */
export const getCurrentJourneyDay = (
  journeyStartDate,
  userEmail = null,
  totalDays = 60,
  holdIntervals = null
) => {
  // Check for admin override first (configured in voiceMinutesManager.js)
  if (userEmail) {
    const override = getJourneyDayOverride(userEmail);
    if (override !== null) {
      console.log(
        `Journey day override active for ${userEmail}: Day ${override}`
      );
      return Math.min(Math.max(override, 1), totalDays);
    }
  }

  // Calendar days since start, minus every non-cancelled pause (including past ones).
  const start = toDateOnly(journeyStartDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = today - start;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  const intervals =
    holdIntervals == null ? getStoredHoldIntervals() : holdIntervals;
  const pausedDays = countHoldDaysOverlapping(start, today, intervals);

  return Math.min(Math.max(diffDays - pausedDays, 1), totalDays);
};

/**
 * Get existing journey from backend or create a new one
 * IMPORTANT: Always checks backend first to prevent generating different journeys on different devices
 * @param {string} userEmail - User's email
 * @param {string} currentLevel - Current CEFR level (e.g., 'B1')
 * @param {string} targetLevel - Target CEFR level (e.g., 'B2')
 * @param {number} assessmentScore - Assessment score
 * @returns {Promise<object>} Journey data (either from backend or newly generated)
 */
export const getOrCreateJourney = async (
  userEmail,
  currentLevel,
  targetLevel,
  assessmentScore
) => {
  console.log(
    `getOrCreateJourney: Checking for existing journey for ${userEmail}...`
  );

  let profileLevel = currentLevel;
  let profileTarget = targetLevel;
  try {
    const profile = await fetchIeltsProfile();
    if (profile?.assessed && profile?.current_level) {
      profileLevel = profile.current_level;
    }
    if (profile?.assessed && profile?.target_level) {
      profileTarget = profile.target_level;
    }
  } catch (error) {
    console.warn('getOrCreateJourney: IELTS profile check failed:', error.message);
  }

  // ALWAYS check backend first to ensure cross-device consistency
  let backendJourney = null;
  let backendFetchFailed = false;

  try {
    backendJourney = await loadJourneyFromBackend(userEmail);
    console.log(
      'getOrCreateJourney: Backend check complete, found:',
      !!backendJourney?.enrolled
    );
  } catch (error) {
    console.warn('getOrCreateJourney: Backend check failed:', error.message);
    backendFetchFailed = true;
  }

  // Check localStorage
  const localJourney = loadJourneyProgress(userEmail);
  console.log(
    'getOrCreateJourney: Local check complete, found:',
    !!localJourney?.enrolled
  );

  // If both exist, use smart merge logic (same as loadJourneyProgressAsync)
  if (backendJourney?.enrolled && localJourney?.enrolled) {
    const key = `languageJourney_${userEmail}`;

    if (isSameJourney(localJourney, backendJourney)) {
      // Same journey - merge progress
      console.log('getOrCreateJourney: Same journey in both sources - merging');
      const merged = mergeJourneyProgress(localJourney, backendJourney);
      localStorage.setItem(key, JSON.stringify(merged));

      // Sync merged progress to backend
      const mergedCount = countCompletedActivities(merged);
      const backendCount = countCompletedActivities(backendJourney);
      if (mergedCount > backendCount) {
        saveJourneyProgress(userEmail, merged).catch(console.error);
      }

      return merged;
    } else {
      // Different journeys - use older (original)
      console.log('getOrCreateJourney: Different journeys - using original');
      const localStart = new Date(localJourney.startDate || 0).getTime();
      const backendStart = new Date(backendJourney.startDate || 0).getTime();

      if (
        !shouldPreferIntentionalJourneyReset(localJourney, backendJourney) &&
        backendStart > 0 &&
        backendStart < localStart
      ) {
        localStorage.setItem(key, JSON.stringify(backendJourney));
        return backendJourney;
      } else {
        // Sync local to backend
        saveJourneyProgress(userEmail, localJourney).catch(console.error);
        return localJourney;
      }
    }
  }

  // If only backend has journey, use it
  if (backendJourney?.enrolled) {
    console.log('getOrCreateJourney: Using backend journey');
    const key = `languageJourney_${userEmail}`;
    localStorage.setItem(key, JSON.stringify(backendJourney));
    return backendJourney;
  }

  // If only local has journey, use it (and sync to backend if fetch didn't fail)
  if (localJourney?.enrolled) {
    console.log('getOrCreateJourney: Using local journey');
    if (!backendFetchFailed) {
      // Backend is reachable but has no data - sync local to backend
      saveJourneyProgress(userEmail, localJourney).catch(console.error);
    }
    return localJourney;
  }

  // No existing journey found - create a new one with deterministic seeding
  console.log(
    'getOrCreateJourney: No existing journey found - creating new journey'
  );
  const startDate = new Date();
  const journey = generateJourneyPlan(
    profileLevel,
    profileTarget,
    startDate,
    userEmail
  );
  journey[0].status = 'current';

  const journeyData = {
    enrolled: true,
    currentLevel: profileLevel,
    targetLevel: profileTarget,
    startDate: startDate.toISOString(),
    currentDay: 1,
    assessmentScore: assessmentScore,
    journey: journey,
  };

  // Save to localStorage and sync to backend
  await saveJourneyProgress(userEmail, journeyData);
  try {
    await updateIeltsProfile({
      current_level: profileLevel,
      target_level: profileTarget,
      enrolled: true,
    });
  } catch (error) {
    console.warn('getOrCreateJourney: failed to persist IELTS profile', error);
  }

  return journeyData;
};

// ==========================================
// Weakness Tracking Utility
// ==========================================

const WEAKNESS_STORAGE_PREFIX = 'liwaza_weakness_';

/**
 * Load weakness data for a user
 * @param {string} userEmail
 * @returns {object} { weakTopics: [], lastUpdated: string }
 */
export const loadWeaknessData = (userEmail) => {
  try {
    const key = `${WEAKNESS_STORAGE_PREFIX}${userEmail}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : { weakTopics: [], lastUpdated: null };
  } catch {
    return { weakTopics: [], lastUpdated: null };
  }
};

/**
 * Save weakness data for a user
 * @param {string} userEmail
 * @param {object} data
 */
const saveWeaknessData = (userEmail, data) => {
  try {
    const key = `${WEAKNESS_STORAGE_PREFIX}${userEmail}`;
    data.lastUpdated = new Date().toISOString();
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving weakness data:', e);
  }
};

/**
 * Track a weakness signal from an activity result.
 * Call this after speaking sessions, checkpoint tests, and course completions.
 *
 * @param {string} userEmail
 * @param {object} signal - The weakness signal:
 *   - type: 'speaking' | 'grammar' | 'vocabulary' | 'listening' | 'reading' | 'writing' | etc.
 *   - reason: 'low_score' | 'low_checkpoint_score' | 'exercises_skipped'
 *   - lessonId?: string (for course-related)
 *   - category?: string (for speaking topics)
 *   - testId?: number (for checkpoint tests)
 *   - score?: number (the score achieved)
 *   - day?: number (journey day)
 *   - name?: string (human-readable name)
 */
export const trackWeakness = (userEmail, signal) => {
  if (!userEmail || !signal?.type || !signal?.reason) return;

  const data = loadWeaknessData(userEmail);

  // Avoid duplicates: check if an identical weakness already exists
  const isDuplicate = data.weakTopics.some(
    (w) =>
      w.type === signal.type &&
      w.lessonId === signal.lessonId &&
      w.category === signal.category &&
      w.testId === signal.testId
  );

  if (isDuplicate) {
    // Update existing entry with latest score/day
    data.weakTopics = data.weakTopics.map((w) => {
      if (
        w.type === signal.type &&
        w.lessonId === signal.lessonId &&
        w.category === signal.category &&
        w.testId === signal.testId
      ) {
        return {
          ...w,
          score: signal.score,
          day: signal.day,
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
  } else {
    data.weakTopics.push({
      ...signal,
      createdAt: new Date().toISOString(),
    });
  }

  // Keep only the most recent 50 entries to prevent unbounded growth
  if (data.weakTopics.length > 50) {
    data.weakTopics = data.weakTopics.slice(-50);
  }

  saveWeaknessData(userEmail, data);
};

/**
 * Check a speaking session result and track weakness if score < assessmentScore
 * @param {string} userEmail
 * @param {number} speakingScore - The band score achieved
 * @param {string} category - Speaking topic category
 * @param {number} day - Journey day
 */
export const checkSpeakingWeakness = (
  userEmail,
  speakingScore,
  category,
  day
) => {
  const journeyData = loadJourneyProgress(userEmail);
  const threshold = journeyData?.assessmentScore || 3.5;
  if (speakingScore < threshold) {
    trackWeakness(userEmail, {
      type: 'speaking',
      reason: 'low_score',
      category,
      score: speakingScore,
      day,
      name: `Speaking: ${category}`,
    });
  }
};

/**
 * Check checkpoint test scores and track weakness if below CEFR threshold
 * @param {string} userEmail
 * @param {string} testType - 'listening' | 'reading' | 'writing'
 * @param {number} testId
 * @param {number} score
 * @param {number} day
 */
export const checkCheckpointWeakness = (
  userEmail,
  testType,
  testId,
  score,
  day
) => {
  const journeyData = loadJourneyProgress(userEmail);
  const threshold = journeyData?.assessmentScore || 3.5;
  if (score < threshold) {
    trackWeakness(userEmail, {
      type: testType,
      reason: 'low_checkpoint_score',
      testId,
      score,
      day,
      name: `${testType.charAt(0).toUpperCase() + testType.slice(1)} Test ${testId}`,
    });
  }
};

/**
 * Track skipped/empty exercises in a completed course
 * @param {string} userEmail
 * @param {string} courseType - 'grammar' | 'vocabulary' | 'listening-course' etc.
 * @param {string} lessonId
 * @param {string} lessonName
 * @param {number} day
 */
export const checkCourseExerciseWeakness = (
  userEmail,
  courseType,
  lessonId,
  lessonName,
  day
) => {
  trackWeakness(userEmail, {
    type: courseType,
    reason: 'exercises_skipped',
    lessonId,
    day,
    name: lessonName,
  });
};

/**
 * Get all weak topics for a user, sorted by recency
 * @param {string} userEmail
 * @returns {Array} Array of weak topic objects
 */
export const getWeakTopics = (userEmail) => {
  const data = loadWeaknessData(userEmail);
  return [...data.weakTopics].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
};

/**
 * Remove a resolved weakness (e.g., after successful revision)
 * @param {string} userEmail
 * @param {string} type
 * @param {string} lessonId
 */
export const resolveWeakness = (userEmail, type, lessonId) => {
  const data = loadWeaknessData(userEmail);
  data.weakTopics = data.weakTopics.filter(
    (w) => !(w.type === type && w.lessonId === lessonId)
  );
  saveWeaknessData(userEmail, data);
};

const languageJourneyData = {
  GRAMMAR_LESSONS,
  VOCABULARY_LESSONS,
  SPEAKING_TOPICS: speakingTopicsData,
  CEFR_TO_DIFFICULTY,
  DAYS_PER_LEVEL,
  DEFAULT_IELTS_TARGET,
  generateJourneyPlan,
  getJourneyTarget,
  getLevelPath,
  calculateTotalJourneyDays,
  getLevelTransitionDurations,
  getLevelSegmentForDay,
  getTodayActivities,
  getJourneyProgress,
  saveJourneyProgress,
  loadJourneyProgress,
  loadJourneyProgressAsync,
  loadJourneyFromBackend,
  getCurrentJourneyDay,
  getOrCreateJourney,
};

export default languageJourneyData;
